import { Store } from '@ngxs/store';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import {
    CompteCourantADebiterOuCrediterModelDto,
    CompteDto,
    CriteresCompteBancaireDtoInput,
    CriteresCompteDtoInput,
    MutationGetAppelComplementaireForReglementArgs,
    MutationGetDefaultJournalPrmFiduArgs,
    MutationGetIdJournalWithIdCptBancAndIdMPayementArgs,
    MutationGetListeCompteCourantFourArgs,
    MutationGetListeCompteGeneriqueArgs
} from 'src/graphql/generated-types';
import gql from 'graphql-tag';
import { catchError, map } from 'rxjs/operators';
import get from 'lodash-es/get';
import { Observable, throwError } from 'rxjs';
import { UserState } from 'src/app/_user/store/user/user.state';
import { ActivityPaymentFragment } from 'src/app/board/activity/fragments/activity-payment.fragment';
import { MoyensPaiement } from 'src/app/board/activity/activity.constants';
import {
    SharedPaymentMethodsState
} from 'src/app/_shared/stores/settlements/shared-payment-methods/shared-payment-methods.state';
import { Injectable } from '@angular/core';

@Injectable()
export class SharedPaymentMethodsService {

    constructor(private readonly store: Store,
                private readonly graphqlService: GraphqlService) {
    }

    getDefaultJournalIdByPaymentMethodId(paymentMethodId: number): Observable<any> {
        return this.graphqlService.mutateOne<'GetDefaultJournalPRMFidu', MutationGetDefaultJournalPrmFiduArgs>({
            mutation: gql`
                mutation GetDefaultJournalPRMFidu($contexte: ContextInfosInput, $PaiMoyId: Float) {
                    GetDefaultJournalPRMFidu(contexte: $contexte, PaiMoyId: $PaiMoyId) {
                        Id
                    }
                }
            `,
            variables: {
                PaiMoyId: paymentMethodId
            }
        }).pipe(
            map(GetDefaultJournalPRMFidu => get(GetDefaultJournalPRMFidu, 'Id', null)),
            catchError(err => throwError(err))
        );
    }

    getDefaultJournalIdByBankIdAndPaymentMethodId(bankAccountId: number, paymentMethodId: number): Observable<any> {
        return this.graphqlService.mutateOne<'GetIdJournalWithIdCptBancAndIdMPayement', MutationGetIdJournalWithIdCptBancAndIdMPayementArgs>({
            mutation: gql`
                mutation GetIdJournalWithIdCptBancAndIdMPayement($contexte: ContextInfosInput, $idCptBnq: Float, $idMoyenPayement: Float) {
                    GetIdJournalWithIdCptBancAndIdMPayement(contexte: $contexte, idCptBnq: $idCptBnq, idMoyenPayement: $idMoyenPayement)
                }
            `,
            variables: {
                idCptBnq: bankAccountId,
                idMoyenPayement: paymentMethodId
            }
        }).pipe(
            map(GetIdJournalWithIdCptBancAndIdMPayement => GetIdJournalWithIdCptBancAndIdMPayement || null),
            catchError(err => throwError(err))
        );
    }

    getAdditionalPaymentData(paymentMethodCode: string): Observable<any> {
        const selectedPaymentMethod = this.store.selectSnapshot(SharedPaymentMethodsState.paymentMethodsList).find(m => m.TypeMoyenPaiement.Code === paymentMethodCode);

        return this.graphqlService.mutateOne<'GetAppelComplementaireForReglement', MutationGetAppelComplementaireForReglementArgs>
        ({
            mutation: gql`
                mutation GetAppelComplementaireForReglement(
                    $contexte: ContextInfosInput,
                    $CriteresCompteBancaire: CriteresCompteBancaireDTOInput,
                    $criteresCompte: CriteresCompteDTOInput,
                    $isReglementFidu: Boolean,
                ) {
                    GetAppelComplementaireForReglement(
                        contexte: $contexte
                        CriteresCompteBancaire: $CriteresCompteBancaire
                        criteresCompte: $criteresCompte
                        isReglementFidu: $isReglementFidu
                    ){
                        ListeCompte {
                            CompteDTO {
                                ... depositAccount
                            }
                        }
                        ListeCompteBancaire {
                            CompteBancaireDTO {
                                Id
                                Libelle
                                ListeCompte511Agreg {
                                    Compte511AgregDTO {
                                        Compte {
                                            Id
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
                ${ActivityPaymentFragment.depositAccount}
            `,
            variables: {
                CriteresCompteBancaire: this.getBankAccountCriteriasForAdditionalPaymentData(paymentMethodCode),
                criteresCompte: {
                    IsBlocked: false,
                    IsClotured: false,
                    IsCollectifFournisseur: true
                },
                isReglementFidu: get(selectedPaymentMethod, 'IsFiduciaire', null),
            }
        }).pipe(
            map(GetAppelComplementaireForReglement => {
                return get(GetAppelComplementaireForReglement, ['ListeCompteBancaire', 'CompteBancaireDTO'], []);
            }),
            catchError(err => throwError(err))
        );
    }

    getGenericAccountsList(criterias: CriteresCompteDtoInput): Observable<CompteDto[]> {
        return this.graphqlService.mutateOne<'GetListeCompteGenerique', MutationGetListeCompteGeneriqueArgs>({
            mutation: gql`
                mutation GetListeCompteGenerique($contexte: ContextInfosInput, $critere: CriteresCompteDTOInput) {
                    GetListeCompteGenerique(contexte: $contexte, critere: $critere){
                        CompteDTO {
                            ... depositAccount
                        }
                    }
                }
                ${ActivityPaymentFragment.depositAccount}
            `,
            variables: {
                critere: criterias
            }
        }).pipe(
            map(GetListeCompteGenerique => GetListeCompteGenerique?.CompteDTO || []),
            catchError(err => throwError(err))
        );
    }

    getAccountsList(criterias: CriteresCompteDtoInput): Observable<CompteCourantADebiterOuCrediterModelDto[]> {
        return this.graphqlService.mutateOne<'GetListeCompteCourantFour', MutationGetListeCompteCourantFourArgs>({
            mutation: gql`
                mutation GetListeCompteCourantFour($contexte: ContextInfosInput, $critereCompte: CriteresCompteDTOInput) {
                    GetListeCompteCourantFour(contexte: $contexte, critereCompte: $critereCompte) {
                        CompteCourantADebiterOuCrediterModelDTO {
                            IdCompte
                            NumeroCompte
                            Solde
                            Libelle
                        }
                    }
                }
            `,
            variables: {
                critereCompte: criterias
            }
        }).pipe(
            map(GetListeCompteCourantFour => {
                return get(GetListeCompteCourantFour, 'CompteCourantADebiterOuCrediterModelDTO', []);
            }),
            catchError(err => throwError(err))
        );
    }

    // <editor-fold defaultstate="collapsed" desc="Private">

    private getBankAccountCriteriasForAdditionalPaymentData(paymentMethodCode: string): CriteresCompteBancaireDtoInput {
        const haveMultiDelegModule = this.store.selectSnapshot(UserState.hasMultiDelegationsModule);
        const currentDelegationId = this.store.selectSnapshot(UserState.currentDelegation).Id;

        const bankAccountCriterias: CriteresCompteBancaireDtoInput = {
            IsClos: false,
            TakeCEDelegation: haveMultiDelegModule,
            AutoriseRemboursement: true
        };

        if (!this.store.selectSnapshot(UserState.isCurrentDelegationGlobal) && !this.store.selectSnapshot(UserState.hasSeparatedAccountingModule)) {
            bankAccountCriterias.IdDelegOuDelegGlobale = haveMultiDelegModule ? currentDelegationId : null;
        } else {
            bankAccountCriterias.IdDelegationCouranteForCompteBancaire = currentDelegationId;
        }

        if ([MoyensPaiement.VirementSEPASurCompte,
            MoyensPaiement.PrelevementSurSalaire,
            MoyensPaiement.ChequeBancaire,
            MoyensPaiement.CarteBancaire,
            MoyensPaiement.PrelevementSEPASurCompte
        ].some(e => e === paymentMethodCode)) {
            bankAccountCriterias.TakeCompte511Agreg = true;
        }

        return bankAccountCriterias;
    }

    // </editor-fold>

}
